import React from "react";
import { FiArrowLeft, FiArrowRight, FiPlus, FiSearch } from "react-icons/fi";
import { PageRenderer, RendererRuntimeProvider, type All8PageContent } from "@bcc/core-renderers";
import { Modal } from "../Modal";
import type { ComponentCategory, ComponentDefinition } from "../../Pages/pageComponentLibrary";
import type { ComponentLibraryPicker } from "./useComponentLibraryPicker";
import ComponentSkeletonThumb from "./ComponentSkeletonThumb";

type PageEditorComponentLibraryModalProps = {
	show: boolean;
	onClose: () => void;
	picker: ComponentLibraryPicker;
	onAdd: (definition: ComponentDefinition) => void;
	runtimeConfig: any;
};

/** French labels for the category filter chips (complete mode only). */
const CATEGORY_LABELS: Record<ComponentCategory, string> = {
	texte: "Texte",
	medias: "Médias",
	cartes: "Cartes & grilles",
	"mise-en-page": "Mise en page",
	donnees: "Données & dynamique",
	formulaires: "Formulaires",
	accueil: "Accueil",
	"pages-dediees": "Pages dédiées"
};

const CATEGORY_ORDER: ComponentCategory[] = [
	"texte",
	"medias",
	"cartes",
	"mise-en-page",
	"donnees",
	"formulaires",
	"accueil",
	"pages-dediees"
];

/**
 * Two-tier component library picker. « Essentiels » shows only the primary
 * blocks with no filtering; « Voir la liste complète » reveals the search bar
 * and category chips. Each card shows a static skeleton thumbnail; the
 * selection detail pane renders a real live preview of the block.
 */
const PageEditorComponentLibraryModal: React.FC<PageEditorComponentLibraryModalProps> = ({
	show,
	onClose,
	picker,
	onAdd,
	runtimeConfig
}) => {
	const {
		mode,
		setMode,
		search,
		setSearch,
		activeCategories,
		toggleCategory,
		clearFilters,
		selection,
		setSelection,
		visibleDefinitions
	} = picker;

	const isEssentials = mode === "essentials";
	// The preview pane belongs to « Voir la liste complète », where you are browsing blocks you may not
	// know. In « Essentiels » these are the eight blocks you use every day: one click adds, no detour.
	const showPreviewPane = !isEssentials && !!selection;

	// « Voir la liste complète » shows smaller, denser cards; essentials stay roomy.
	const compactCards = !isEssentials;
	// Grid density: narrower while the preview pane is open, otherwise pack in as
	// many columns as possible : most in the (denser) complete list.
	const gridColsClass = showPreviewPane
		? "grid-cols-2 xl:grid-cols-3"
		: isEssentials
			? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
			: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";

	const handleClose = () => {
		onClose();
	};

	return (
		<Modal show={show} onHide={handleClose} size="full85" centered>
			<Modal.Header
				closeButton
				className="rounded-t-xl border-none bg-gradient-to-r from-white via-slate-50 to-slate-100 px-6 py-5"
			>
				<div className="min-w-0 space-y-1">
					<Modal.Title className="text-xl font-semibold text-slate-900">
						Bibliothèque de composants
					</Modal.Title>
					{isEssentials ? (
						<p className="text-sm text-slate-500">Composants essentiels</p>
					) : (
						<p className="text-sm text-slate-500">Tous les composants disponibles</p>
					)}
				</div>
				<div className="flex items-center gap-2">
					{isEssentials ? (
						<button
							type="button"
							onClick={() => setMode("all")}
							className="inline-flex items-center gap-2 rounded-full bg-[#005CA9] px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0F2849]"
						>
							Voir la liste complète
							<FiArrowRight className="h-4 w-4" />
						</button>
					) : (
						<button
							type="button"
							onClick={() => {
								clearFilters();
								setMode("essentials");
							}}
							className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
						>
							<FiArrowLeft className="h-4 w-4" />
							Essentiels
						</button>
					)}
				</div>
			</Modal.Header>
			<Modal.Body className="bg-slate-50/80">
				{!isEssentials && (
					<div className="mb-4 space-y-3">
						<div className="relative">
							<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
							<input
								className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-slate-400 focus:outline-none"
								placeholder="Rechercher un composant…"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								aria-label="Rechercher un composant"
							/>
						</div>
						<div className="flex flex-wrap gap-2">
							{CATEGORY_ORDER.map((category) => {
								const active = activeCategories.includes(category);
								return (
									<button
										key={category}
										type="button"
										onClick={() => toggleCategory(category)}
										aria-pressed={active}
										className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
											active
												? "bg-[#005CA9] text-white shadow-sm"
												: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
										}`}
									>
										{CATEGORY_LABELS[category]}
									</button>
								);
							})}
						</div>
					</div>
				)}

				<div className="flex flex-col gap-4 lg:flex-row lg:items-start">
					{/* Card grid takes the full width until a component is selected, so as many
					    components as possible are visible; the preview pane appears on selection.
					    Complete-mode cards are denser (more columns) and a bit smaller. */}
					<div className="space-y-3 lg:flex-1 lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1">
						{visibleDefinitions.length === 0 ? (
							<p className="py-10 text-center text-sm text-slate-500">
								Aucun composant ne correspond à votre recherche.
							</p>
						) : (
							<div className={`grid gap-3 ${gridColsClass}`}>
								{visibleDefinitions.map((definition) => {
									const isSelected = selection?.type === definition.type;
									return (
										<button
											type="button"
											key={definition.type}
											data-component-card={definition.type}
											aria-pressed={isSelected}
											// Essentiels: one click adds it, full stop : no preview to detour through.
											// Complete list: click toggles the preview (a second click, or « Annuler »,
											// clears it) and double-click adds straight to the page.
											onClick={() =>
												isEssentials ? onAdd(definition) : setSelection(isSelected ? null : definition)
											}
											onDoubleClick={() => onAdd(definition)}
											className={`flex h-full flex-col rounded-xl border text-left transition ${
												compactCards ? "p-2" : "p-3"
											} ${
												isSelected
													? "border-[#005CA9] bg-white shadow-sm ring-1 ring-[#005CA9]"
													: "border-slate-200 bg-white hover:border-slate-300"
											}`}
										>
											<ComponentSkeletonThumb definition={definition} compact={compactCards} />
											<p
												className={`mt-2 font-semibold text-slate-900 ${
													compactCards ? "text-xs" : "text-sm"
												}`}
											>
												{definition.title}
											</p>
											<p
												className={`text-slate-600 line-clamp-2 ${
													compactCards ? "text-[11px] leading-snug" : "text-xs"
												}`}
											>
												{definition.description}
											</p>
										</button>
									);
								})}
							</div>
						)}
					</div>

					{showPreviewPane && selection && (
						<div className="lg:w-[38%] xl:w-[34%] lg:flex-shrink-0">
							<div className="rounded-2xl border border-slate-200 bg-white shadow-sm lg:sticky lg:top-0">
								<div className="border-b border-slate-100 px-4 py-3">
									<p className="text-sm font-semibold text-slate-900">{selection.title}</p>
									<p className="text-sm text-slate-600">{selection.description}</p>
								</div>
								<div className="space-y-4 p-4">
									<div className="max-h-[46vh] overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3">
										<RendererRuntimeProvider value={runtimeConfig}>
											<PageRenderer
												components={
													selection.previewComponents ??
													([
														{
															componentType: selection.type,
															key: "preview",
															content: JSON.parse(JSON.stringify(selection.defaultContent))
														}
													] as All8PageContent)
												}
											/>
										</RendererRuntimeProvider>
									</div>
									<div className="flex items-center justify-end gap-2">
										<button
											type="button"
											onClick={() => setSelection(null)}
											className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
										>
											Annuler
										</button>
										<button
											type="button"
											onClick={() => onAdd(selection)}
											className="inline-flex items-center gap-2 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0F2849]"
										>
											<FiPlus className="h-4 w-4" /> Ajouter à la page
										</button>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</Modal.Body>
		</Modal>
	);
};

export default PageEditorComponentLibraryModal;
